import { MapClickContext } from './map-click-context.models';
import { isValidMapCoordinate } from './processing-request-params.util';

export { getEffectiveZoom } from '@app/utils/app-zoom.util';

/** Approximate rendered size of the menu, used to keep it inside the viewport. */
export const MAP_REQUEST_MENU_WIDTH = 300;
export const MAP_REQUEST_MENU_HEIGHT = 48;
const VIEWPORT_MARGIN = 8;

export interface MenuPosition {
  top: number;
  left: number;
}

/** Shifts the menu so that it stays fully inside the viewport, keeping a small margin. */
export function clampMenuPosition(
  clientX: number,
  clientY: number,
  viewport: { width: number; height: number },
  menu: { width: number; height: number } = { width: MAP_REQUEST_MENU_WIDTH, height: MAP_REQUEST_MENU_HEIGHT },
): MenuPosition {
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - menu.width - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewport.height - menu.height - VIEWPORT_MARGIN);
  return {
    left: Math.min(Math.max(clientX, VIEWPORT_MARGIN), maxLeft),
    top: Math.min(Math.max(clientY, VIEWPORT_MARGIN), maxTop),
  };
}

/**
 * The menu is offered only to permitted users in the map browsing mode.
 * While a drawing or measuring mode is active, right-click keeps its existing meaning.
 */
export function canOpenMapRequestMenu(state: { permitted: boolean; drawingActive: boolean }): boolean {
  return state.permitted && !state.drawingActive;
}

/** Creates the context of a right-click; returns null for coordinates that are missing or out of range. */
export function toMapClickContext(event: { lat?: unknown; lng?: unknown } | null | undefined): MapClickContext | null {
  const lat = event?.lat;
  const lng = event?.lng;
  if (!isValidMapCoordinate(lat, lng)) {
    return null;
  }
  return { lat: lat as number, lng: lng as number };
}

/**
 * Menu position for a `position: fixed` element that lives inside a page zoomed by `zoom`:
 * the pointer and the viewport are converted into the zoomed coordinate space, then clamped there,
 * so the menu renders at the pointer and stays fully visible.
 */
export function menuPositionForPointer(
  clientX: number,
  clientY: number,
  viewport: { width: number; height: number },
  zoom = 1,
): MenuPosition {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return clampMenuPosition(clientX / z, clientY / z, { width: viewport.width / z, height: viewport.height / z });
}
